import React from 'react'
import Modal from './Modal'
import { FiAlertTriangle, FiTrash2, FiInfo, FiCheckCircle } from 'react-icons/fi'

export type ConfirmDialogType = 'danger' | 'warning' | 'info' | 'success'

interface ConfirmDialogProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    title: string
    message: string
    confirmText?: string
    cancelText?: string
    type?: ConfirmDialogType
    loading?: boolean
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    type = 'danger',
    loading = false
}) => {
    const handleConfirm = () => {
        onConfirm()
        // Don't close automatically - let the parent handle it after async operation
    }

    const getIconAndColors = () => {
        switch (type) {
            case 'danger':
                return {
                    icon: <FiTrash2 className="w-6 h-6" />,
                    iconBg: 'bg-red-100 dark:bg-red-900/20',
                    iconColor: 'text-red-600 dark:text-red-400',
                    confirmBg: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
                    confirmText: 'text-white'
                }
            case 'warning':
                return {
                    icon: <FiAlertTriangle className="w-6 h-6" />,
                    iconBg: 'bg-yellow-100 dark:bg-yellow-900/20',
                    iconColor: 'text-yellow-600 dark:text-yellow-400',
                    confirmBg: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
                    confirmText: 'text-white'
                }
            case 'info':
                return {
                    icon: <FiInfo className="w-6 h-6" />,
                    iconBg: 'bg-blue-100 dark:bg-blue-900/20',
                    iconColor: 'text-blue-600 dark:text-blue-400',
                    confirmBg: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
                    confirmText: 'text-white'
                }
            case 'success':
                return {
                    icon: <FiCheckCircle className="w-6 h-6" />,
                    iconBg: 'bg-green-100 dark:bg-green-900/20',
                    iconColor: 'text-green-600 dark:text-green-400',
                    confirmBg: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
                    confirmText: 'text-white'
                }
        }
    }

    const { icon, iconBg, iconColor, confirmBg, confirmText: confirmTextColor } = getIconAndColors()

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="sm">
            <div className="p-6">
                <div className="sm:flex sm:items-start">
                    {/* Icon */}
                    <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${iconBg} sm:mx-0 sm:h-10 sm:w-10`}>
                        <div className={iconColor}>
                            {icon}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            {title}
                        </h3>
                        <div className="mt-2">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {message}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={loading}
                        className={`
                            w-full justify-center rounded-lg border border-transparent px-4 py-2 text-base font-medium
                            ${confirmBg} ${confirmTextColor}
                            shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2
                            sm:ml-3 sm:w-auto sm:text-sm
                            disabled:opacity-50 disabled:cursor-not-allowed
                            transition-colors duration-200
                        `}
                    >
                        {loading ? (
                            <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                <span className="ml-2">Processing...</span>
                            </div>
                        ) : (
                            confirmText
                        )}
                    </button>

                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="
                            mt-3 w-full justify-center rounded-lg border border-gray-300 dark:border-gray-600
                            bg-white dark:bg-gray-700 px-4 py-2 text-base font-medium
                            text-gray-700 dark:text-gray-300
                            shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600
                            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                            sm:mt-0 sm:w-auto sm:text-sm
                            disabled:opacity-50 disabled:cursor-not-allowed
                            transition-colors duration-200
                        "
                    >
                        {cancelText}
                    </button>
                </div>
            </div>
        </Modal>
    )
}

export default ConfirmDialog